import { Device } from "../model/Device";
import { Trailer } from "../model/Trailer";
import Api from "../services/api";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import constants from "../constants";

interface ISlimAssetSearch {
    id?: string;
    name?: string;
    fromDate?: string;
    groups?: IIdEntity[];
}

function callAssetApiAsync(api: Api, method: string, params: {
        typeName: string;
        entity?: Device | Trailer;
        search?: ISlimAssetSearch;
        resultsLimit?: number;
    }): Promise {
    return new Promise((resolve, reject) => {
        api.call(method, params, resolve, reject);
    });
}

export function getAsset(api: Api, search: ISlimAssetSearch, typeName: string, resultsLimit = 1): Promise {
    return callAssetApiAsync(api, "Get", {
        typeName,
        search,
        resultsLimit
    });
}

export function addAsset(api: Api, entity: Device | Trailer, typeName: string): Promise {
    return callAssetApiAsync(api, "Add", {
        typeName,
        entity
    });
}

const { ASSET_GROUP_ID, TRAILER_GROUP_ID, ENTIRE_ORGANIZATION_GROUP_ID } = constants.GROUPS;

function getGroupAssetTypeAncestors(api: Api): Promise {
    return new Promise((resolve, reject) => {
        api.call("Get", {
            "typeName": "Group",
            "search": {
                "ancestors": [{ id: ASSET_GROUP_ID }]
            },
            "resultsLimit": 10000
        }, resolve, reject);
    });
}

export const getTrailerGroups = (api: Api, currentUserGroups: DeepReadonlyArray<IGroup>): Promise<IIdEntity[]> => {
        return getGroupAssetTypeAncestors(api).then((results: IGroup[]) => {
        if (results.length) {
            // groups that are not allowed to be associated with Trailer types (any child of ASSET_GROUP_ID that is not in TRAILER_GROUP_ID)
            const forbiddenAssetTypeGroups: string[] = [];
            // could be any child of TRAILER_GROUP_ID
            let trailerTypeGroups: string[] = [];
            const assetTypeChildrenIds = results.find(r => r.id === ASSET_GROUP_ID)?.children;
            if (!assetTypeChildrenIds) {
                throw new Error(`${ ASSET_GROUP_ID } group must have children`);
            }
            const assetTypeChildrenGroups = results.filter(group => assetTypeChildrenIds.some(a => a.id === group.id));
            const getChildrenOfGroupRecursive = (group: IGroup): IGroup[] => {
                const result: IGroup[] = [];
                result.push(group);
                if (group && group.children?.length) {
                    group?.children?.forEach(child => {
                        const populatedChild = results.find(g => g.id === child.id);
                        const children = getChildrenOfGroupRecursive(populatedChild as IGroup);
                        result.length && result.push(...children);
                    });
                }
                return result;
            };
            assetTypeChildrenGroups?.forEach(group => {
                if (group.id === TRAILER_GROUP_ID) {
                    const mainAssetTypeChildren = getChildrenOfGroupRecursive(group);
                    trailerTypeGroups.push(...mainAssetTypeChildren.map(c => c.id));
                    // remove parent TRAILER_GROUP_ID here so it's easier to find correct user groups
                    trailerTypeGroups = trailerTypeGroups.filter(g => g !== TRAILER_GROUP_ID);
                } else {
                    const mainAssetTypeChildren = getChildrenOfGroupRecursive(group);
                    forbiddenAssetTypeGroups.push(...mainAssetTypeChildren.map(c => c.id));
                }
            });
            const userGroups = currentUserGroups.filter(g => ![ENTIRE_ORGANIZATION_GROUP_ID, ASSET_GROUP_ID, TRAILER_GROUP_ID].some(id => id === g.id));
            // remove any forbidden asset type groups from current user groups
            const userGroupsExcludingNotAllowedAssetTypes = userGroups.filter(g => !forbiddenAssetTypeGroups.some(id => id === g.id)).map(g => ({ id: g.id }));
            // find if user groups have any children under TRAILER_GROUP_ID, if not, include TRAILER_GROUP_ID
            if (!userGroups.some(g => trailerTypeGroups.some(id => id === g.id))) {
                userGroupsExcludingNotAllowedAssetTypes.push({ id: TRAILER_GROUP_ID });
            }
            return userGroupsExcludingNotAllowedAssetTypes;
        }
        return [{ id: TRAILER_GROUP_ID }];
    });
};
